import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Brain, ArrowLeft, User, Mail, Phone, Calendar, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const TRAIT_COLORS = {
  'Openness': '#8B5CF6',
  'Conscientiousness': '#3B82F6',
  'Extraversion': '#F59E0B',
  'Agreeableness': '#10B981',
  'Emotional Stability': '#EC4899',
  'Leadership': '#6366F1',
  'Teamwork': '#14B8A6',
  'Integrity': '#8B5CF6',
  'Stress Tolerance': '#EF4444',
  'Decision Making': '#F97316'
};

function PsychometricDetail() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAttempt();
  }, [attemptId]);

  const fetchAttempt = async () => {
    try {
      const response = await api.get(`/admin/psychometric/attempt/${attemptId}`);
      setAttempt(response.data);
    } catch (error) {
      console.error('Error fetching attempt:', error);
      toast.error('Failed to load attempt details');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-blue-100';
    if (score >= 40) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Strong';
    if (score >= 40) return 'Moderate';
    return 'Needs Improvement';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Brain className="w-12 h-12 text-purple-600 animate-pulse" />
        </div>
      </Layout>
    );
  }

  if (!attempt) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Attempt not found</p>
          <Button onClick={() => navigate('/admin/psychometric')} className="mt-4">
            Back to Psychometric Results
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/psychometric')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Results
        </Button>

        {/* Header */}
        <Card className="p-6 mb-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Brain className="w-12 h-12" />
              <div>
                <h1 className="text-2xl font-bold" data-testid="detail-title">Psychometric Assessment Detail</h1>
                <p className="text-purple-200">{attempt.test?.title || 'Assessment Results'}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Student Info */}
        <Card className="p-6 mb-6" data-testid="student-info-card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Student Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium text-gray-900">{attempt.user?.name || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-900">{attempt.user?.email || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium text-gray-900">{attempt.user?.phone || 'N/A'}</p>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="font-medium text-gray-900">
                  {attempt.end_time ? new Date(attempt.end_time).toLocaleString() : 'In Progress'}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Overall Score */}
        <Card className="p-6 mb-6" data-testid="overall-score-card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Overall Score</h2>
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-5xl font-bold ${getScoreColor(attempt.overall_score)}`}>
                {attempt.overall_score?.toFixed(1)}%
              </div>
              <div className={`text-sm font-medium mt-1 px-3 py-1 rounded-full inline-block ${getScoreBg(attempt.overall_score)} ${getScoreColor(attempt.overall_score)}`}>
                {getScoreLabel(attempt.overall_score)}
              </div>
            </div>
            <div className="w-40 h-40">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <circle cx="50" cy="50" r="40" fill="none" stroke="#E5E7EB" strokeWidth="8" />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke={attempt.overall_score >= 60 ? '#10B981' : attempt.overall_score >= 40 ? '#F59E0B' : '#EF4444'}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${(attempt.overall_score / 100) * 251.2} 251.2`}
                  transform="rotate(-90 50 50)"
                />
                <text x="50" y="55" textAnchor="middle" className="text-xl font-bold" fill="#374151">
                  {attempt.overall_score?.toFixed(0)}%
                </text>
              </svg>
            </div>
          </div>
        </Card>

        {/* Trait Scores */}
        <Card className="p-6 mb-6" data-testid="trait-scores-card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Trait Scores</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {attempt.trait_scores && Object.entries(attempt.trait_scores).map(([trait, score]) => {
              const traitDetail = attempt.interpretation?.trait_details?.[trait];
              return (
                <div key={trait} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium text-gray-900">{trait}</span>
                    <span className={`text-lg font-bold ${getScoreColor(score)}`}>{score?.toFixed(1)}%</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${score}%`,
                        backgroundColor: TRAIT_COLORS[trait] || '#8B5CF6'
                      }}
                    />
                  </div>
                  <div className={`text-xs px-2 py-1 rounded-full inline-block ${getScoreBg(score)} ${getScoreColor(score)}`}>
                    {traitDetail?.level || getScoreLabel(score)}
                  </div>
                  {traitDetail?.description && (
                    <p className="text-sm text-gray-600 mt-2">{traitDetail.description}</p>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Interpretation */}
        {attempt.interpretation && (
          <>
            {/* Strengths & Weaknesses */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Card className="p-6" data-testid="strengths-card">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Strengths</h2>
                </div>
                {attempt.interpretation.strengths?.length > 0 ? (
                  <ul className="space-y-2">
                    {attempt.interpretation.strengths.map((strength, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-green-700">
                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                        {strength}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">No notable strengths identified.</p>
                )}
              </Card>
              
              <Card className="p-6" data-testid="weaknesses-card">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingDown className="w-5 h-5 text-orange-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Areas for Development</h2>
                </div>
                {attempt.interpretation.weaknesses?.length > 0 ? (
                  <ul className="space-y-2">
                    {attempt.interpretation.weaknesses.map((weakness, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-orange-700">
                        <div className="w-2 h-2 bg-orange-500 rounded-full" />
                        {weakness}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">No significant areas for improvement identified.</p>
                )}
              </Card>
            </div>

            {/* Summary */}
            <Card className="p-6 mb-6" data-testid="summary-card">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Assessment Summary</h2>
              <p className="text-gray-700 leading-relaxed">{attempt.interpretation.summary}</p>
            </Card>

            {/* Development Suggestions */}
            {attempt.interpretation.development_suggestions?.length > 0 && (
              <Card className="p-6 mb-6" data-testid="suggestions-card">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Development Recommendations</h2>
                <ul className="space-y-3">
                  {attempt.interpretation.development_suggestions.map((suggestion, idx) => (
                    <li key={idx} className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                      <div className="w-6 h-6 bg-purple-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-sm font-medium text-purple-700">{idx + 1}</span>
                      </div>
                      <span className="text-gray-700">{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </>
        )}

        {/* Response Details */}
        {attempt.answers && attempt.answers.length > 0 && (
          <Card className="p-6" data-testid="responses-card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Response Details</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Q#</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Trait</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Response</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Scoring</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {attempt.answers.map((answer, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-3">{answer.question_number}</td>
                      <td className="py-2 px-3">{answer.trait}</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          answer.response >= 4 ? 'bg-green-100 text-green-700' :
                          answer.response >= 3 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {answer.response} - {
                            answer.response === 5 ? 'Strongly Agree' :
                            answer.response === 4 ? 'Agree' :
                            answer.response === 3 ? 'Neutral' :
                            answer.response === 2 ? 'Disagree' :
                            'Strongly Disagree'
                          }
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        {answer.reverse_scoring ? (
                          <span className="text-orange-600">Reverse</span>
                        ) : (
                          <span className="text-green-600">Normal</span>
                        )}
                      </td>
                      <td className="py-2 px-3 font-medium">{answer.score?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <div className="mt-6 text-center">
          <Button onClick={() => navigate('/admin/psychometric')} variant="outline" data-testid="back-btn-bottom">
            Back to Results
          </Button>
        </div>
      </div>
    </Layout>
  );
}

export default PsychometricDetail;
